import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { safeAnchorHref } from '../../../lib/link';
import { cn } from '../../../lib/utils';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraIconTile } from '../ensigns/icon-tile';
import { SpecraText } from '../ensigns/typography';
/** One prioritized action: semantic icon, work label, metadata, and affordance. */ export function SpecraActionRow({ className, item, renderLink, trailing = '→', ...props }) {
    // Inert unless the row renders as a link or a button: a rejected fallback
    // href with no handler is a plain div and carries no arrow.
    const fallbackHref = item.href && !renderLink ? safeAnchorHref(item.href) : undefined;
    const linked = Boolean(item.href && renderLink) || Boolean(fallbackHref);
    const inert = Boolean(item.actions) || !linked && !item.onClick;
    const sharedProps = {
        className: cn('flex w-full gap-3.5 py-3 text-left transition-colors', item.description ? 'items-start' : 'items-center', !inert && `cursor-pointer hover:bg-[color:var(--surface-hover)] ${specraFocusRing}`, className),
        ...props
    };
    const content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            item.icon ? /*#__PURE__*/ _jsx(SpecraIconTile, {
                tone: item.tone ?? 'neutral',
                children: item.icon
            }) : null,
            item.description ? /*#__PURE__*/ _jsxs("span", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        className: "truncate",
                        size: "body-sm",
                        children: item.label
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        className: "mt-0.5 truncate",
                        size: "meta",
                        tone: "muted",
                        children: item.description
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                className: "min-w-0 flex-1",
                size: "body-sm",
                children: item.label
            }),
            item.meta ? /*#__PURE__*/ _jsx(SpecraText, {
                className: "shrink-0 tabular-nums",
                size: "meta",
                tone: "muted",
                children: item.meta
            }) : null,
            item.actions ? /*#__PURE__*/ _jsx("span", {
                className: "flex shrink-0 items-center gap-2",
                children: item.actions
            }) : null,
            trailing !== null && !inert ? /*#__PURE__*/ _jsx(SpecraText, {
                "aria-hidden": "true",
                className: "shrink-0 opacity-50",
                tone: "muted",
                children: trailing
            }) : null
        ]
    });
    if (item.actions) {
        return /*#__PURE__*/ _jsx("div", {
            ...sharedProps,
            children: content
        });
    }
    if (item.href && renderLink) {
        return renderLink({
            ...sharedProps,
            children: content,
            href: item.href
        });
    }
    const Component = fallbackHref ? 'a' : item.onClick ? 'button' : 'div';
    const interactiveProps = fallbackHref ? {
        href: fallbackHref
    } : item.onClick ? {
        onClick: item.onClick,
        type: 'button'
    } : {};
    return /*#__PURE__*/ _jsx(Component, {
        ...sharedProps,
        ...interactiveProps,
        children: content
    });
}
